import type { AuthResponse } from '@/types';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

const ACCESS_KEY = 'conduril-partes-access';
const REFRESH_KEY = 'conduril-partes-refresh';

/** Broadcast when the session can no longer be refreshed, so the app returns to login. */
export const SESSION_EXPIRED_EVENT = 'conduril-partes:session-expired';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(auth: AuthResponse) {
    localStorage.setItem(ACCESS_KEY, auth.accessToken);
    localStorage.setItem(REFRESH_KEY, auth.refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  form?: FormData;
  auth?: boolean; // attach the access token (default true)
  signal?: AbortSignal;
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refresh = tokenStore.refresh;
  if (!refresh) return false;

  // De-duplicate concurrent refreshes.
  refreshPromise ??= (async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) return false;
      const auth = (await res.json()) as AuthResponse;
      tokenStore.set(auth);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function toError(res: Response): Promise<ApiError> {
  let message = res.statusText || 'Erro';
  let errors: Record<string, string[]> | undefined;
  try {
    const problem = await res.json();
    if (problem?.title) message = problem.title;
    if (problem?.errors) errors = problem.errors;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(res.status, message, errors);
}

async function doFetch(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  const auth = options.auth ?? true;
  if (auth && tokenStore.access) headers.Authorization = `Bearer ${tokenStore.access}`;

  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form; // browser sets multipart boundary
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
  });
}

/** Perform an authenticated JSON request, transparently refreshing on 401 once. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await doFetch(path, options);

  if (res.status === 401 && (options.auth ?? true)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doFetch(path, options);
    } else {
      tokenStore.clear();
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      throw new ApiError(401, 'sessionExpired');
    }
  }

  if (!res.ok) throw await toError(res);

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return (await res.json()) as T;
  return undefined as T;
}

/** Fetch a binary file (e.g. CSV export) as a Blob, with auth + refresh. */
export async function apiBlob(path: string): Promise<{ blob: Blob; fileName: string }> {
  let res = await doFetch(path, {});
  if (res.status === 401) {
    if (await tryRefresh()) res = await doFetch(path, {});
    else {
      tokenStore.clear();
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      throw new ApiError(401, 'sessionExpired');
    }
  }
  if (!res.ok) throw await toError(res);

  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match ? decodeURIComponent(match[1]) : 'export.csv';
  return { blob: await res.blob(), fileName };
}
